import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../api/client';
import { useTranslation } from 'react-i18next';

export default function ResetPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [tokenUsed, setTokenUsed] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError(t('reset.errShort'));
      return;
    }
    if (password !== confirm) {
      setError(t('reset.errMismatch'));
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail === 'TOKEN_ALREADY_USED' || /utilizado|already.used/i.test(detail)) {
        setTokenUsed(true);
      } else if (detail === 'TOKEN_EXPIRED' || detail === 'TOKEN_INVALID' || /expirado|invalido|expired|invalid/i.test(detail)) {
        setTokenExpired(true);
      } else {
        setError(detail || t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={S.hexBg} />
      <div style={S.card}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <span style={{ fontSize: 40, display: 'block', marginBottom: 8 }}>🍯</span>
          <h1 style={{ fontSize: 24, fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: '#2C1A0E' }}>
            {t('reset.title')}
          </h1>
          <p style={{ fontSize: 13, color: '#8B6355', marginTop: 6 }}>
            {t('reset.subtitle')}
          </p>
        </div>

        {!token ? (
          <div>
            <div style={S.error}>{t('reset.invalidToken')}</div>
            <Link to="/forgot-password" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 12, marginTop: 4 }}>
              {t('reset.requestNew')}
            </Link>
          </div>
        ) : tokenExpired ? (
          <div>
            <div style={S.expiredBox}>
              <span style={{ fontSize: 36, display: 'block', marginBottom: 10 }}>⏰</span>
              <p style={{ margin: '0 0 6px', fontWeight: 700, fontSize: 15 }}>{t('reset.errExpired')}</p>
              <p style={{ margin: 0, fontSize: 13, color: '#8B6355' }}>{t('reset.expiredHint')}</p>
            </div>
            <Link to="/forgot-password" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 12, marginTop: 4 }}>
              {t('reset.requestNew')}
            </Link>
          </div>
        ) : tokenUsed ? (
          <div>
            <div style={S.usedBox}>
              <span style={{ fontSize: 36, display: 'block', marginBottom: 10 }}>🔒</span>
              <p style={{ margin: '0 0 6px', fontWeight: 700, fontSize: 15 }}>{t('reset.errUsed')}</p>
              <p style={{ margin: 0, fontSize: 13, color: '#8B6355' }}>{t('reset.usedHint')}</p>
            </div>
            <Link to="/forgot-password" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 12, marginTop: 4 }}>
              {t('reset.requestNew')}
            </Link>
          </div>
        ) : success ? (
          <div style={S.success}>
            {t('reset.successMsg')}
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="form-group">
              <label className="form-label">{t('reset.newPasswordLabel')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                placeholder="••••••••"
                minLength={6}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('reset.confirmLabel')}</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="form-input"
                placeholder="••••••••"
                minLength={6}
                required
              />
            </div>
            {error && (
              <div style={S.error}>{error}</div>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', padding: 12 }}
              disabled={loading}
            >
              {loading ? '...' : t('reset.updateBtn')}
            </button>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Link to="/login" style={S.link}>{t('common.back')}</Link>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: 'linear-gradient(135deg, #FDF0DC 0%, #F5C47A 50%, #E8922A 100%)', position: 'relative' },
  hexBg: { position: 'absolute', inset: 0, opacity: 0.06, backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='52' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0 L60 17.3 L60 34.6 L30 52 L0 34.6 L0 17.3Z' fill='%232C1A0E'/%3E%3C/svg%3E\")", backgroundSize: '60px 52px' },
  card: { background: 'rgba(255,252,248,0.96)', backdropFilter: 'blur(12px)', borderRadius: 24, padding: 40, width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(44,26,14,0.2)', position: 'relative', zIndex: 1 },
  error: { background: '#FDECEA', color: '#C0392B', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 },
  success: { background: '#E8F5E9', color: '#2E7D32', padding: '14px 16px', borderRadius: 10, fontSize: 14, lineHeight: 1.5, textAlign: 'center' },
  expiredBox: { background: '#FFF8E1', color: '#5D4037', padding: '18px 16px', borderRadius: 12, fontSize: 14, lineHeight: 1.6, textAlign: 'center', marginBottom: 16, border: '1px solid #FFE082' },
  usedBox: { background: '#F3E5F5', color: '#4A148C', padding: '18px 16px', borderRadius: 12, fontSize: 14, lineHeight: 1.6, textAlign: 'center', marginBottom: 16, border: '1px solid #CE93D8' },
  link: { color: '#8B6355', fontSize: 13, textDecoration: 'none' },
};
